import { useParams, useSearchParams } from 'react-router-dom'
import { Skeleton } from '@/components/ui/skeleton'
import { AlertCircle } from 'lucide-react'
import { useSite, useSiteLogs } from '../hooks/useSites'
import { SiteDetailHeader } from '../components/SiteDetailHeader'
import { SiteDetailSidebar, type SiteTab } from '../components/SiteDetailSidebar'
import { OverviewTab } from '../components/tabs/OverviewTab'
import { ActivityTab } from '../components/tabs/ActivityTab'
import { UsersTab } from '../components/tabs/UsersTab'
import { SettingsTab } from '../components/tabs/SettingsTab'
import { DiagnosticsTab } from '../components/tabs/DiagnosticsTab'
import { UptimeTab } from '../components/tabs/UptimeTab'
import { GeoBlockingTab } from '../components/tabs/GeoBlockingTab'
import { SeoHealthTab } from '../components/tabs/SeoHealthTab'
import { ManageTab } from '../components/tabs/ManageTab'

const VALID_TABS: SiteTab[] = ['overview', 'activity', 'users', 'settings', 'diagnostics', 'uptime', 'seo-health', 'geo-blocking', 'manage']

export function SiteDetailPage() {
  const { domain } = useParams<{ domain: string }>()
  const [searchParams, setSearchParams] = useSearchParams()

  // Get active tab from URL, defaulting to 'overview'
  const tabParam = searchParams.get('tab') as SiteTab | null
  const activeTab: SiteTab = tabParam && VALID_TABS.includes(tabParam) ? tabParam : 'overview'

  // Decode domain in case it contains special characters
  const decodedDomain = domain ? decodeURIComponent(domain) : ''

  const { data: siteData, isLoading, error } = useSite(decodedDomain)
  const site = siteData?.site
  const { data: logs = [] } = useSiteLogs(site?.domain ?? null, 20)

  // Tab change handler - updates URL search params
  const handleTabChange = (tab: SiteTab) => {
    setSearchParams({ tab })
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col min-h-0">
        <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="container flex h-14 items-center gap-4 px-4">
            <Skeleton className="h-8 w-24" />
            <Skeleton className="h-5 w-48" />
          </div>
        </header>
        <div className="flex md:hidden gap-2 border-b px-4 py-2 overflow-x-auto">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-9 w-20 shrink-0" />
          ))}
        </div>
        <div className="flex min-h-0">
          <div className="hidden md:flex w-48 shrink-0 flex-col gap-1 border-r p-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} className="h-9 w-full" />
            ))}
          </div>
          <div className="flex-1 p-3 md:p-6">
            <div className="space-y-4">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Error state
  if (error || !site) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8">
        <AlertCircle className="h-12 w-12 text-destructive mb-4" />
        <h2 className="text-xl font-semibold mb-2">Site Not Found</h2>
        <p className="text-muted-foreground text-center max-w-md">
          The site "{decodedDomain}" could not be found or you don't have permission to view it.
        </p>
      </div>
    )
  }

  return (
    <div className="flex flex-col min-h-0">
      <SiteDetailHeader site={site} />

      {/* Mobile: horizontal tabs */}
      <div className="md:hidden">
        <SiteDetailSidebar activeTab={activeTab} onTabChange={handleTabChange} />
      </div>

      <div className="flex min-h-0">
        {/* Desktop: vertical sidebar */}
        <div className="hidden md:block">
          <SiteDetailSidebar activeTab={activeTab} onTabChange={handleTabChange} />
        </div>

        <div className="flex-1 p-3 md:p-6">
          {activeTab === 'overview' && <OverviewTab site={site} logs={logs} />}
          {activeTab === 'activity' && <ActivityTab site={site} />}
          {activeTab === 'users' && <UsersTab site={site} />}
          {activeTab === 'settings' && <SettingsTab site={site} />}
          {activeTab === 'diagnostics' && <DiagnosticsTab site={site} />}
          {activeTab === 'uptime' && <UptimeTab site={site} />}
          {activeTab === 'seo-health' && <SeoHealthTab site={site} />}
          {activeTab === 'geo-blocking' && <GeoBlockingTab site={site} />}
          {activeTab === 'manage' && <ManageTab site={site} />}
        </div>
      </div>
    </div>
  )
}
